import { useGetUiFeatureFlags } from '@/hooks/useUiFeatureFlags';
import { defaultUiFeatureFlags, type UiFeatureFlags } from '@/types/FeatureFlags';
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';

type FeatureFlagsContextValue = {
  flags: UiFeatureFlags;
  isLoading: boolean;
  isFetching: boolean;
  isFetchedAfterMount: boolean;
  isError: boolean;
  refreshFlags: () => Promise<void>;
  lastFetched: number | null;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isFetching, isFetchedAfterMount, isError, refetch } = useGetUiFeatureFlags();

  const flags = useMemo<UiFeatureFlags>(
    () => ({
      viewVendors: data?.viewVendors ?? defaultUiFeatureFlags.viewVendors,
      viewDeliveryFailureWarning:
        data?.viewDeliveryFailureWarning ?? defaultUiFeatureFlags.viewDeliveryFailureWarning,
      viewAllocation: data?.viewAllocation ?? defaultUiFeatureFlags.viewAllocation,
      viewAttachmentTypeSearchFilters: data?.viewAttachmentTypeSearchFilters ?? defaultUiFeatureFlags.viewAttachmentTypeSearchFilters,
      viewClosingSearchFilters: data?.viewClosingSearchFilters ?? defaultUiFeatureFlags.viewClosingSearchFilters,
      viewOrderEventSearch: data?.viewOrderEventSearch ?? defaultUiFeatureFlags.viewOrderEventSearch,
      viewClearToCloseDocumentationReport: data?.viewClearToCloseDocumentationReport ?? defaultUiFeatureFlags.viewClearToCloseDocumentationReport,
      viewEmailSubscription:
        data?.viewEmailSubscription ?? defaultUiFeatureFlags.viewEmailSubscription,
      viewEventTypeAdvancedFilters: data?.viewEventTypeAdvancedFilters ?? defaultUiFeatureFlags.viewEventTypeAdvancedFilters,
      showAllAllocationOperatorOptions: data?.showAllAllocationOperatorOptions ?? defaultUiFeatureFlags.showAllAllocationOperatorOptions,
      createAttributeDictionary: data?.createAttributeDictionary ?? defaultUiFeatureFlags.createAttributeDictionary,
      tenantConfigFeatureControl: data?.tenantConfigFeatureControl ?? defaultUiFeatureFlags.tenantConfigFeatureControl,
    }),
    [
      data?.viewVendors,
      data?.viewDeliveryFailureWarning,
      data?.viewAllocation,
      data?.viewClosingSearchFilters,
      data?.viewOrderEventSearch,
      data?.viewAttachmentTypeSearchFilters,
      data?.viewClearToCloseDocumentationReport,
      data?.viewEmailSubscription,
      data?.viewEventTypeAdvancedFilters,
      data?.showAllAllocationOperatorOptions,
      data?.createAttributeDictionary,
      data?.tenantConfigFeatureControl,
    ]
  );

  const refreshFlags = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      flags,
      isLoading: isLoading && !data,
      isFetching,
      isFetchedAfterMount,
      isError,
      refreshFlags,
      lastFetched: data?.lastFetched ?? null,
    }),
    [flags, isLoading, data, isFetching, isFetchedAfterMount, isError, refreshFlags]
  );
  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useUiFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useUiFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}
